import { X, Send } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useEffect, useRef, useState } from 'react';

interface ChatMessage {
  sender: string;
  text?: string;
  message?: string;
  timestamp: number;
  isHost?: boolean;
  id?: string;
  senderId?: string;
}

interface ChatPanelProps {
  isMobileView: boolean;
  chatMessages: ChatMessage[];
  newMessage: string;
  setNewMessage: (msg: string) => void;
  setActivePanel: (panel: 'chat' | 'notes' | 'annotate' | null) => void;
  sendMessage: () => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  isHost?: boolean;
  currentSocketId?: string;
}

export function ChatPanel({
  isMobileView,
  chatMessages,
  newMessage,
  setNewMessage,
  setActivePanel,
  sendMessage,
  handleKeyPress,
  isHost = false,
  currentSocketId,
}: ChatPanelProps) {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  // Track viewport height changes for keyboard detection
  useEffect(() => {
    const handleViewportResize = () => {
      // Use visualViewport API for accurate keyboard detection
      if (window.visualViewport) {
        setViewportHeight(window.visualViewport.height);
      } else {
        setViewportHeight(window.innerHeight);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
      window.visualViewport.addEventListener('scroll', handleViewportResize);

      return () => {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
        window.visualViewport.removeEventListener('scroll', handleViewportResize);
      };
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [chatMessages]);

  const handleSendMessage = () => {
    if (newMessage.trim()) {
      sendMessage();
      inputRef.current?.focus();
    }
  };

  // ---------- MOBILE: full-screen modal ----------
  if (isMobileView) {
    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[75]"
          onClick={() => setActivePanel(null)}
        />

        {/* Modal - Centered on mobile, adjusts for keyboard */}
        <div
          data-panel="chat"
          className="fixed left-1/2 -translate-x-1/2 z-[85] bg-white flex flex-col rounded-2xl shadow-2xl border-2 border-t-4 border-gray-300"
          style={{
            width: 'calc(100vw - 12px)', // Extended from 32px to 12px (20px wider)
            maxWidth: '95vw',
            height: `${Math.min(viewportHeight * 0.6, 500)}px`,
            maxHeight: `${viewportHeight - 100}px`,
            // Centered vertically when keyboard is closed
            top: viewportHeight < window.innerHeight * 0.8 ? '20px' : '50%',
            transform: viewportHeight < window.innerHeight * 0.8 ? 'translateX(-50%)' : 'translate(-50%, -50%)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b bg-white flex-shrink-0 rounded-t-2xl">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Send className="h-4 w-4 text-primary" strokeWidth={2} />
              </div>
              <h3 className="font-semibold text-gray-900">Chat</h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActivePanel(null)}
              className="h-8 w-8 p-0 rounded-full hover:bg-gray-100"
            >
              <X className="h-4 w-4 text-gray-600" />
            </Button>
          </div>

          {/* Messages */}
          <div
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"
            style={{
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {chatMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mb-3">
                  <Send className="h-8 w-8 text-gray-400" strokeWidth={2} />
                </div>
                <p className="text-gray-500 text-sm font-medium">No messages yet</p>
                <p className="text-gray-400 text-xs mt-1">Start a conversation</p>
              </div>
            ) : (
              <>
                {chatMessages.map((msg, idx) => {
                  // Better logic: check if message is from current user
                  // 1. Compare senderId if available
                  // 2. Fall back to isHost comparison
                  const isMe = msg.senderId
                    ? msg.senderId === currentSocketId
                    : msg.isHost === isHost;

                  const messageText = msg.text || msg.message || '';
                  const senderName = msg.sender || (msg.isHost ? 'Host' : 'Guest');

                  return (
                    <div
                      key={msg.id || idx}
                      className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className="flex flex-col max-w-[80%]">
                        {/* Sender name above bubble */}
                        <span className={`text-xs text-gray-500 mb-1 px-1 ${isMe ? 'text-right' : 'text-left'}`}>
                          {isMe ? 'You' : senderName}
                        </span>

                        <div
                          className={`rounded-2xl px-3 py-2 text-sm ${
                            isMe ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white text-gray-800 rounded-bl-sm'
                          } shadow-sm`}
                        >
                          <p className="break-words leading-relaxed">{messageText}</p>
                          <p
                            className={`mt-1 text-[10px] ${
                              isMe ? 'text-blue-100' : 'text-gray-400'
                            }`}
                          >
                            {new Date(msg.timestamp).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t bg-white flex-shrink-0 rounded-b-2xl">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  } else {
                    handleKeyPress(e);
                  }
                }}
                placeholder="Type a message..."
                className="flex-1 px-4 py-2.5 bg-gray-100 border border-transparent rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 placeholder-gray-500 text-sm"
                style={{ fontSize: '16px' }}
              />
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                className="px-4 py-2.5 bg-primary hover:bg-primary/90 active:bg-primary/80 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-full text-sm font-medium text-white transition-colors"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ---------- DESKTOP: top-center overlay (like mobile) ----------
  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[75]"
        onClick={() => setActivePanel(null)}
      />

      {/* Modal */}
      <div
        data-panel="chat"
        className="fixed left-1/2 top-8 -translate-x-1/2 z-[85] bg-white flex flex-col rounded-2xl shadow-2xl border border-gray-200"
        style={{
          width: '90%',
          maxWidth: '600px',
          height: '70vh',
          maxHeight: '600px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b bg-white flex-shrink-0 rounded-t-2xl">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Send className="h-4 w-4 text-primary" strokeWidth={2} />
          </div>
          <h3 className="font-semibold text-gray-900">Chat</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setActivePanel(null)}
          className="h-8 w-8 p-0 rounded-full hover:bg-gray-100"
        >
          <X className="h-4 w-4 text-gray-600" />
        </Button>
      </div>

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"
        style={{
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {chatMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mb-3">
              <Send className="h-8 w-8 text-gray-400" strokeWidth={2} />
            </div>
            <p className="text-gray-500 text-sm font-medium">No messages yet</p>
            <p className="text-gray-400 text-xs mt-1">Start a conversation</p>
          </div>
        ) : (
          <>
            {chatMessages.map((msg, idx) => {
              // Better logic: check if message is from current user
              const isMe = msg.senderId
                ? msg.senderId === currentSocketId
                : msg.isHost === isHost;

              const messageText = msg.text || msg.message || '';
              const senderName = msg.sender || (msg.isHost ? 'Host' : 'Guest');

              return (
                <div key={msg.id || idx} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                  <div className="flex flex-col max-w-[80%]">
                    {/* Sender name above bubble */}
                    <span className={`text-xs text-gray-500 mb-1 px-1 ${isMe ? 'text-right' : 'text-left'}`}>
                      {isMe ? 'You' : senderName}
                    </span>

                    <div
                      className={`rounded-2xl px-3 py-2 text-sm ${
                        isMe ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white text-gray-800 rounded-bl-sm'
                      } shadow-sm`}
                    >
                      <p className="break-words leading-relaxed">{messageText}</p>
                      <p
                        className={`mt-1 text-[10px] ${
                          isMe ? 'text-blue-100' : 'text-gray-400'
                        }`}
                      >
                        {new Date(msg.timestamp).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t bg-white flex-shrink-0 rounded-b-2xl">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Type a message..."
            className="flex-1 px-4 py-2.5 bg-gray-100 border border-transparent rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 placeholder-gray-500 text-sm"
          />
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            className="px-4 py-2.5 bg-primary hover:bg-primary/90 active:bg-primary/80 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-full text-sm font-medium text-white transition-colors"
          >
            Send
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
